import type { Category } from './categories.js';
import { getAll, queued, transact } from './db.js';
import { categoryKey, categoryDepth, descendants } from './category-tree.js';
export { categoryKey } from './category-tree.js';
/*
  Ключи — прежние: «income.4» («Прочие поступления») выписка берёт по
  умолчанию. Новая «Кешбэк и проценты» (15.10.2026) — со своим ключом, но в
  списке стоит рядом с похожими.
*/
export const INCOME_CATEGORIES: Category[] = ([
  ['income.0', 'Зарплата', 'money-bill'], ['income.1', 'Подработка', 'briefcase'],
  ['income.5', 'Кешбэк и проценты', 'coins'], ['income.2', 'Подарок', 'gift'],
  ['income.3', 'Продажа вещей', 'tag'], ['income.4', 'Прочие поступления', 'arrow-trend-up'],
] as const).map(([id, name, icon]) => ({
  id, name, kind: 'income' as const, parentId: null, defaultPart: null,
  icon, color: 'blue',
}));

export async function saveCategory(input: { id?: string; name: string; kind: 'expense' | 'income'; defaultPart: Category['defaultPart']; archived?: boolean; parentId?: string | null; icon?: string; color?: string }): Promise<Category> {
  return queued('categories', () => transact(['categories'], async db => {
    const all = await db.all<Category>('categories');
    const old = input.id ? all.find(c => c.id === input.id) : undefined;
    if (input.id && !old) throw new Error('Категория не найдена');
    const name = input.name.trim().replace(/\s+/g, ' ');
    if (!name || name.length > 60) throw new Error('Название должно содержать от 1 до 60 символов');
    const kind = old?.kind ?? (old ? 'expense' : input.kind);
    if ((!old || categoryKey(old.name) !== categoryKey(name)) && all.some(c => c.id !== input.id && (c.kind ?? 'expense') === kind && categoryKey(c.name) === categoryKey(name))) throw new Error('Такая категория уже есть. Выбери её или восстанови из архива.');
    const parentId = input.parentId === undefined ? old?.parentId ?? null : input.parentId;
    const parent = all.find(c => c.id === parentId);
    const branch = old ? descendants(all, old.id) : new Set<string>();
    if (parentId && (!parent || (parent.kind ?? 'expense') !== kind)) throw new Error('Родительская категория должна быть того же типа');
    if (parentId && branch.has(parentId)) throw new Error('Нельзя вложить категорию в саму себя или своего потомка');
    const archived = input.archived ?? old?.archived ?? false;
    if (parent?.archived && !archived) throw new Error('Сначала восстанови родительскую категорию');
    const icon = (input.icon ?? old?.icon ?? (kind === 'income' ? 'arrow-trend-up' : 'circle-ellipsis')).trim();
    if (icon.length > 32) throw new Error('Выбери короткую иконку');
    const color = input.color ?? old?.color;
    const category: Category = { ...old, id: old?.id ?? `custom.${crypto.randomUUID()}`, name, kind, parentId,
      icon, ...(color ? { color } : {}), defaultPart: kind === 'income' ? null : input.defaultPart, archived };
    const next = [...all.filter(c => c.id !== category.id), category];
    const moved = descendants(next, category.id);
    if ([...moved].some(id => categoryDepth(next, id) > 3)) throw new Error('Можно создать не больше трёх уровней категорий');
    db.put('categories', category);
    // Archiving a branch keeps every historical ID. Restoring the root never resurrects previously archived children silently.
    if (archived) for (const c of all) if (c.id !== category.id && branch.has(c.id)) db.put('categories', { ...c, archived: true });
    return category;
  }));
}

/**
 * Порядок категорий, как человек расставил (28.10.2026) — номером в самой
 * категории, как у счетов: синхронизация переносит его вместе с ней.
 */
export async function saveCategoryOrder(ids: readonly string[]): Promise<void> {
  await queued('categories', () => transact(['categories'], async db => {
    const all = await db.all<Category>('categories');
    ids.forEach((id, i) => {
      const c = all.find(x => x.id === id);
      if (c && c.order !== i) db.put('categories', { ...c, order: i });
    });
  }));
}

/** Категории в порядке человека; не расставленные — после, как лежали. */
export function sortCategories<T extends Category>(list: readonly T[]): T[] {
  return list.map((c, i) => [c, c.order ?? 100_000 + i] as const).sort((a, b) => a[1] - b[1]).map(([c]) => c);
}

/** Ссылается ли запись (на любой глубине) на категорию. */
const refersTo = (id: string) => {
  const refers = (v: unknown): boolean => {
    if (!v || typeof v !== 'object') return false;
    if (Array.isArray(v)) return v.some(refers);
    const row = v as Record<string, unknown>;
    return row.categoryId === id || Object.values(row).some(refers);
  };
  return refers;
};
const USES = ['transactions', 'obligations', 'plannedExpenses', 'receiptImports'] as const;

/**
 * Можно ли удалить категорию насовсем: нет вложенных и ни одна запись на неё
 * не ссылается. Иначе — только архив: операции и «Аналитика» показывают её
 * по-прежнему под её именем.
 *
 * Форма спрашивает это заранее и кнопку «Удалить» показывает только тогда,
 * когда удаление пройдёт, — а не сообщает об отказе после подтверждения.
 */
export async function categoryDeletable(id: string): Promise<boolean> {
  const all = await getAll<Category>('categories');
  if (all.some(c => c.parentId === id)) return false;
  const refers = refersTo(id);
  for (const store of USES) if ((await getAll(store)).some(refers)) return false;
  return true;
}

/** Only unused leaves may be physically removed. Used categories remain available in history via archive. */
export async function deleteCategory(id: string): Promise<void> {
  return queued('categories', () => transact(['categories', ...USES], async db => {
    const all = await db.all<Category>('categories');
    if (!all.some(c => c.id === id)) return;
    if (all.some(c => c.parentId === id)) throw new Error('У категории есть вложенные категории. Архивируй ветку или сначала перенеси их.');
    const refers = refersTo(id);
    for (const store of USES) {
      if ((await db.all(store)).some(refers)) throw new Error('Категория используется в записях. Архивируй её — история сохранится.');
    }
    db.del('categories', id);
  }));
}
